import { Box, Collapse, Dialog, Divider, IconButton, Paper, Tab, Tabs, Typography, useTheme } from "@mui/material"
import { useCallback, useEffect, useRef, useState } from "react"
import parse from 'html-react-parser'

import DataLoadingSpinner from '../components/common/DataLoadingSpinner'
import noTutorialsImg from '../assets/upload-assignment.svg'
import { Icons } from "../utils/utilities"
import api from '../service/api'
import TryAgainMessageBlock from '../components/common/TryAgainMessageBlock'
import VideoPlayer from '../components/common/VideoPlayer'

const FacultyFaqs = [
    {
        section: "Online classes",
        faq: [
            {
                question: "How to join a class?",
                answer: "Allow permissions such as camera and microphone in the browser. Go to home/dashboard page and under online classes section, join the scheduled class by clicking on join"
            },
            {
                question: "How to take attendance?",
                answer: "Once joined the sessions attendence will be taken automatically in certain interval of time"
            },
            {
                question: "How to schedule online classes?",
                answer: "Add class to your time table using ERP and mark it as <em className='faq-em'>online</em>"
            }
        ]
    },
    {
        section: "Exam",
        faq: [
            {
                question: "How to create an exam?",
                answer: `
                                <div className="faq-steps">
                                    <div className="faq-step-box">
                                        <span className="faq-step">1</span>
                                        <p>Click in the <em>+</em> at the bottom right corner of the screen in exams page.</p>
                                    </div>
                                    <div className="faq-step-box">
                                        <span className="faq-step">2</span>
                                        <p>Fill all the required fields such as exam name, description, topics, exam start and end time and batches.</p>
                                    </div>
                                    <div className="faq-step-box">
                                        <span className="faq-step">3</span>
                                        <p>Click on <em>create</em> button at the bottom.</p>
                                    </div>
                                </div>
                `
            }, {
                question: "How to add questions to an exam?",
                answer: `
                        <div className="faq-steps">
                        <h3 className='faq-answer-header'>From a file</h3>
                            <div className="faq-step-box">
                                <span className="faq-step">1</span>
                                <p>Click on the <em>Import Question</em> button in exam page.</p>
                            </div>
                            <div className="faq-step-box">
                                <span className="faq-step">2</span>
                                <p>Choose a text file in <a target="blank" href="https://docs.moodle.org/401/en/Aiken_format">aiken</a> format to import questions .</p>
                            </div>
                        </div>
                        <div className="faq-steps">
                        <h3 className='faq-answer-header'>From the UI</h3>
                            <div className="faq-step-box">
                                <span className="faq-step">1</span>
                                <p>Click on the <em>Add Question</em> button in exam page.</p>
                            </div>
                            <div className="faq-step-box">
                                <span className="faq-step">2</span>
                                <p>Fill all the required fields.</p>
                            </div>
                            <div className="faq-step-box">
                                <span className="faq-step">3</span>
                                <p>Click on the <em>Add</em> button to add new question.</p>
                            </div>
                        </div>
                `
            },
            {
                question: "Is the question restricted to english language only?",
                answer: "No. question can be framed in any language in text file or through UI."
            }
        ]
    },
    {
        section: "Study Material",
        faq: [
            {
                question: "What are the supported file formats?",
                answer: `.pdf, .docx, .ppsx, .pptx, .odp`
            },
            {
                question: "Can files be added after creation of study material?",
                answer: `Yes`
            },
            {
                question: "Which students have access to my notes?",
                answer: `Any student with Alive access will be able to access any study material on Alive`
            }
        ]
    }
]

const StudentFaqs = [
    {
        section: "Online Classes",
        faq: [
            {
                question: "How to join a class?",
                answer: "Allow permissions such as camera and microphone in the browser. Go to home/dashboard page and under online classes section, join the scheduled class by clicking on join"
            }
        ]
    },
    {
        section: "Exam",
        faq: [
            {
                question: "Can exams be taken on mobile devices?",
                answer: "Yes. But a device with larger sceen is recommended."
            },
            {
                question: "Is fullscreen mandatory while taking the exam?",
                answer: "Yes. If fullscreen mode is exited the exam will be ended."
            },
            {
                question: "What happens if window focus is changes during the exam?",
                answer: "The exam will be ended."
            }
        ]
    },
    {
        section: "Study Material",
        faq: [
            {
                question: "Can study materials of other branch are accessible",
                answer: "Yes."
            },
            {
                question: "Is it possible to dowload entire study material?",
                answer: "Yes. In a compressed zip file"
            }
        ]
    }
]


const TutorialBox = ( { tutorial, setSelectedTutorial } ) => {

    const [previewPlaying, setPreviewPlaying] = useState( false )

    const { palette, border } = useTheme()

    const previewVideoRef = useRef()


    const handleOnMouseLeavePreviewVideo = async () => {
        try {
            if ( previewVideoRef.current ) {
                previewVideoRef.current.muted = true
                if ( !previewVideoRef.current.paused )
                    await previewVideoRef.current?.pause()
            }
        } catch ( err ) {
            console.log( "Failed to pause preview video" )
        }
    }

    const handleOnMouseOverPreviewVideo = async () => {
        try {
            if ( previewVideoRef.current ) {
                previewVideoRef.current.muted = true
                if ( previewVideoRef.current.paused )
                    await previewVideoRef.current?.play()
            }
        } catch ( err ) {
            console.log( "Failed to pause preview video" )
        }
    }



    return (
        <Box onClick={() => setSelectedTutorial( tutorial )} overflow="clip" borderRadius="10px" border={border[1]} key={tutorial.id}>
            <Box sx={{ cursor: "pointer", width: "100%", aspectRatio: "16 / 9" }} onMouseOver={handleOnMouseOverPreviewVideo} onMouseEnter={handleOnMouseOverPreviewVideo} onMouseLeave={handleOnMouseLeavePreviewVideo} position="relative" width="100%"  >
                <Box onPlay={() => setPreviewPlaying( true )} onPause={() => setPreviewPlaying( false )} ref={previewVideoRef} component="video" src={tutorial.signedUrl} width="100%" sx={{ objectFit: "fit", width: "100%", aspectRatio: "16 / 9" }} />
                {!previewPlaying && <Box fontSize="35px" color="white" position="absolute" sx={{ inset: "0" }} bgcolor="rgba(0,0,0,0.6)" display="flex" flexDirection="column" height="100%" alignItems="center" justifyContent="center" component="span">
                    <Box component="span" className='remove-on-hover' >{Icons.default.PlayCircleFilledWhiteIcon}</Box>
                    <Typography variant='subtitle2' component="span" fontSize="10px" textAlign="center" >
                        {previewPlaying ? "Playing..." : "Bring mouse over to preview"}
                    </Typography>
                </Box>}
            </Box>
            <Box padding="10px">
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                        fontSize: '18px',
                        lineHeight: '1em',

                        /* two-line clamp */
                        display: '-webkit-box',
                        WebkitLineClamp: 2,         // <- number of lines
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        // optional: preserve word-break behavior
                        whiteSpace: 'normal',
                    }}
                >
                    {tutorial.title}
                </Typography>
                <Box display="flex" gap="10px" alignItems="center">
                    <Typography borderRadius="5px" width="fit-content" variant='subtitle2' bgcolor={palette.contentBg} border={border[1]} padding="2px 5px" >{tutorial.module}</Typography>
                    <Typography borderRadius="5px" width="fit-content" variant='subtitle2' bgcolor={palette.contentBg} border={border[1]} padding="2px 5px" >{tutorial.tutorial_for}</Typography>
                </Box>
            </Box>
        </Box>
    )
}

const FAQs = ( { auth } ) => {

    const [selectedTutorial, setSelectedTutorial] = useState( null )
    const [collapseState, setCollapseState] = useState( {} )
    const [tabValue, setTabValue] = useState( "tutorials" )
    const [loading, setLoading] = useState( true )
    const [error, setError] = useState( null )

    const [tutorials, setTutorials] = useState( [] )

    const { palette, border } = useTheme()

    const getTutotrials = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data: tutorials } } = await api.tutorials.fetchTutorials()
            setTutorials( tutorials )
            setError( null )
        } catch ( err ) {
            console.log( err )
            setError( { code: err?.response.status || 500, message: err?.response?.data?.message || "SOmething went wrong while fetching tutorials." } )
        } finally {
            setLoading( false )
        }
    }, [] )

    useEffect( () => {
        getTutotrials()
    }, [getTutotrials] )

    return (
        <Box padding="20px" overflow="auto" flexGrow={1}>
            <Paper>
                <Typography padding="20px" variant="h5">Alive Support</Typography>

                <Tabs value={tabValue} onChange={( event, newVal ) => setTabValue( newVal )}>
                    <Tab label="Tutorials" value="tutorials"> </Tab>
                    <Tab label="FAQs" value="FAQ"> </Tab>
                </Tabs>

                {tabValue === "FAQ" && <Box padding="20px" paddingTop="0">
                    {auth === "faculty" && FacultyFaqs.map( section => (
                        <Box marginTop="20px" border='1px solid #rgba(0,0,0,0.2)' key={section.section}>
                            <Typography color="textSecondary" variant="subtitle1" gutterBottom>{section.section}</Typography>
                            {section.faq.map( ( item, k ) => <Box key={`${section.section}-${k}`} bgcolor="#fff" borderTop="1px solid rgba(0,0,0,0.1)" borderBottom="1px solid rgba(0,0,0,0.1)">
                                <Box onClick={() => { setCollapseState( { [`${section.section}-${k}`]: !collapseState[`${section.section}-${k}`] } ) }} sx={{ cursor: "pointer" }}>
                                    <Box display="flex" alignItems="center" gap="10px" padding="10px" sx={{ background: collapseState[`${section.section}-${k}`] ? "#eee" : "#fff", "&:hover": { background: "#eee" } }}>
                                        {collapseState[`${section.section}-${k}`] ? Icons.default.RemoveIcon : Icons.default.AddIcon}
                                        <Typography variant="subtitle1" fontWeight="600">{item.question}</Typography>
                                    </Box>
                                </Box>
                                <Collapse sx={{ background: collapseState[`${section.section}-${k}`] ? "#f5f5f5" : "#fff" }} in={collapseState[`${section.section}-${k}`]}>
                                    <Divider />
                                    <Box margin="20px" borderRadius="0 5px 5px 0" padding="10px">
                                        {parse( item.answer )}
                                    </Box>
                                </Collapse>
                            </Box> )}
                        </Box>
                    ) )}
                    {auth === "student" && StudentFaqs.map( section => (
                        <Box marginTop="20px" border='1px solid #rgba(0,0,0,0.2)' key={section.section}>
                            <Typography color="textSecondary" variant="subtitle1" gutterBottom>{section.section}</Typography>
                            {section.faq.map( ( item, k ) => <Box key={`${section.section}-${k}`} bgcolor="#fff" borderTop="1px solid rgba(0,0,0,0.1)" borderBottom="1px solid rgba(0,0,0,0.1)">
                                <Box onClick={() => { setCollapseState( { [`${section.section}-${k}`]: !collapseState[`${section.section}-${k}`] } ) }} sx={{ cursor: "pointer" }}>
                                    <Box display="flex" alignItems="center" gap="10px" padding="10px" sx={{ background: collapseState[`${section.section}-${k}`] ? "#eee" : "#fff", "&:hover": { background: "#eee" } }}>
                                        {collapseState[`${section.section}-${k}`] ? Icons.default.RemoveIcon : Icons.default.AddIcon}
                                        <Typography variant="subtitle1" fontWeight="600">{item.question}</Typography>
                                    </Box>
                                </Box>
                                <Collapse sx={{ background: collapseState[`${section.section}-${k}`] ? "#f5f5f5" : "#fff" }} in={collapseState[`${section.section}-${k}`]}>
                                    <Divider />
                                    <Box margin="20px" borderRadius="0 5px 5px 0" padding="10px">
                                        {parse( item.answer )}
                                    </Box>
                                </Collapse>
                            </Box> )}
                        </Box>
                    ) )}
                </Box>}

                {tabValue === "tutorials" && <Box padding="20px">

                    {selectedTutorial && <Dialog open={selectedTutorial} slotProps={{ paper: { sx: { width: "95%", maxWidth: "1024px" } } }} >
                        <Box borderBottom={border[1]} padding="10px 20px" display="flex" justifyContent="space-between" gap="20px" alignItems="center" >
                            <Typography variant='h6'>{selectedTutorial.title}</Typography>
                            <IconButton size='small' onClick={() => setSelectedTutorial( null )} >
                                {Icons.default.CloseIcon}
                            </IconButton>
                        </Box>
                        <Box display="flex" justifyContent="center" alignItems="center">
                            <VideoPlayer src={selectedTutorial.signedUrl} />
                        </Box>
                        <Box padding="10px 20px">
                            <Typography>{selectedTutorial.description || "No description!"}</Typography>
                        </Box>
                    </Dialog>}

                    {loading && <DataLoadingSpinner waitingMessage="Fetching tutorials..." />}
                    {!loading && !error && tutorials.length > 0 && <Box></Box>}
                    {!loading && !error && tutorials.length === 0 && <Box padding="20px" borderRadius="10px" display="flex" alignItems="center" flexDirection="column" gap="10px" bgcolor={palette.contentBg}>
                        <Box component="img" src={noTutorialsImg} alt='No tutorials uploaded' width="150px" />
                        <Typography variant='subtitle2' textAlign="center" >Tutorials are not uploaded.</Typography>
                    </Box>}
                    {tutorials.length > 0 && !error && <Box borderRadius="10px" display="grid" alignItems="flex-start" flexDirection="column" gap="10px" gridTemplateColumns={{ md: "repeat(4,1fr)", xs: "repeat(1,1fr)", sm: "repeat(2,1fr)" }} >
                        {tutorials.map( tutorial => (
                            <TutorialBox setSelectedTutorial={setSelectedTutorial} tutorial={tutorial} />
                        ) )}
                    </Box>}
                    {error && <TryAgainMessageBlock loading={loading} getData={getTutotrials} err={error.message} code={error.code} />}
                </Box>}
            </Paper>
        </Box>
    )
}

export default FAQs
